import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Loader2, Copy, Clock, ListChecks, CheckCircle2, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_META = {
  pending: { label: "Beklemede", color: "#F59E0B", bg: "rgba(245,158,11,0.15)", icon: Clock },
  queued: { label: "Sırada", color: "#60A5FA", bg: "rgba(59,130,246,0.15)", icon: ListChecks },
  completed: { label: "Tamamlandı", color: "#10B981", bg: "rgba(16,185,129,0.15)", icon: CheckCircle2 },
};

export const AdminWithdrawalsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/withdrawals");
      setRows(data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopyalandı`);
  };

  const act = async (id, kind) => {
    setActing(id + kind);
    try {
      await api.post(`/admin/withdrawals/${id}/${kind}`);
      toast.success(kind === "queue" ? "Talep sıraya alındı" : "Talep tamamlandı");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setActing(null);
    }
  };

  const pending = rows.filter((r) => r.status !== "completed").length;

  return (
    <div data-testid="admin-withdrawals-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
        <Banknote className="w-4 h-4 text-emerald-400" />
        <h3 className="text-sm font-bold">Para Çekimleri · Referans Komisyonu</h3>
        {pending > 0 && <span className="text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B" }}>{pending} bekliyor</span>}
        <button data-testid="refresh-withdrawals-button" onClick={load} className="ml-auto inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1.5 rounded-lg border transition-all hover:brightness-110" style={{ borderColor: "var(--border-subtle)" }}>
          <RefreshCw className="w-3.5 h-3.5" /> Yenile
        </button>
      </div>

      <div className="px-5 sm:px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-sm" style={{ color: "var(--text-muted)" }}>Henüz çekim talebi yok</div>
        ) : (
          <div className="space-y-3">
            {rows.map((w) => {
              const m = STATUS_META[w.status] || STATUS_META.pending;
              const Icon = m.icon;
              return (
                <div key={w.id} data-testid={`admin-withdrawal-${w.id}`} className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">{w.username}</span>
                      <span className="font-mono-term text-sm font-bold" style={{ color: "#10B981" }}>{w.amount} USDT</span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: m.bg, color: m.color }}>
                      <Icon className="w-3 h-3" /> {m.label}
                    </span>
                  </div>

                  {/* wallet info — visible when queued (and always to admin) */}
                  {(w.status === "queued" || w.status === "completed") && (
                    <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 mb-2" style={{ background: "var(--bg-card)" }}>
                      <div className="min-w-0">
                        <div className="text-[9px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Gönderilecek Cüzdan (BEP20)</div>
                        <div data-testid={`withdrawal-address-${w.id}`} className="text-[12px] font-mono-term truncate" style={{ color: "var(--text-primary)" }}>{w.address}</div>
                      </div>
                      <button onClick={() => copy(w.address, "Cüzdan")} className="p-2 rounded-lg border shrink-0" style={{ borderColor: "var(--border-subtle)" }}><Copy className="w-4 h-4" /></button>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {w.status === "pending" && (
                      <button data-testid={`queue-withdrawal-${w.id}`} onClick={() => act(w.id, "queue")} disabled={acting === w.id + "queue"}
                        className="inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white transition-all hover:brightness-110 disabled:opacity-50" style={{ background: "#3B82F6" }}>
                        {acting === w.id + "queue" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />} Sıraya Al
                      </button>
                    )}
                    {w.status === "queued" && (
                      <button data-testid={`complete-withdrawal-${w.id}`} onClick={() => act(w.id, "complete")} disabled={acting === w.id + "complete"}
                        className="inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white transition-all hover:brightness-110 disabled:opacity-50" style={{ background: "linear-gradient(135deg,#10B981,#059669)" }}>
                        {acting === w.id + "complete" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Tamamlandı
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
